/**
 * Modal persetujuan pemrosesan data pribadi sebelum unggah foto profil / foto KTP.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

export interface PersonalDataUploadConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dipanggil setelah pengguna mencentang persetujuan dan menekan tombol konfirmasi. */
  onConfirm: () => void;
  /** Teks tombol utama, mis. "Unggah foto" atau "Simpan dan unggah". */
  confirmLabel?: string;
}

export function PersonalDataUploadConsentDialog({
  open,
  onOpenChange,
  onConfirm,
  confirmLabel = "Lanjutkan unggah",
}: PersonalDataUploadConsentDialogProps) {
  const [agreed, setAgreed] = React.useState(false);

  React.useEffect(() => {
    if (open) setAgreed(false);
  }, [open]);

  const handleConfirm = () => {
    if (!agreed) return;
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl" style={{ maxHeight: '90vh' }}>
        <div className="shrink-0 space-y-2 p-6 pb-2">
          <DialogHeader>
            <DialogTitle>Persetujuan pemrosesan data pribadi</DialogTitle>
            <DialogDescription>
              Baca pernyataan berikut sebelum mengunggah foto atau dokumen identitas.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="text-foreground flex-1 overflow-y-auto px-6 text-sm leading-relaxed" style={{ minHeight: 0 }}>
          <div className="space-y-4 rounded-md border bg-muted/30 p-4">
            <p className="font-medium">Dengan ini saya menyatakan bahwa:</p>

            <section className="space-y-2">
              <p className="font-medium">1.</p>
              <p>
                Saya secara sadar dan tanpa paksaan memberikan persetujuan kepada Organisasi Amatir
                Radio Indonesia untuk:
              </p>
              <ul className="ml-4 list-disc space-y-1 pl-2">
                <li>Mengumpulkan, menyimpan, memproses, dan menggunakan data pribadi saya;</li>
                <li>
                  Termasuk namun tidak terbatas pada:
                  <ul className="mt-1 list-disc space-y-1 pl-4">
                    <li>Nama lengkap</li>
                    <li>Nomor Induk Kependudukan (NIK)</li>
                    <li>Salinan/foto Kartu Tanda Penduduk (KTP)</li>
                    <li>Alamat</li>
                    <li>Nomor telepon</li>
                    <li>Alamat email</li>
                    <li>Data keanggotaan amatir radio</li>
                    <li>Foto</li>
                  </ul>
                </li>
              </ul>
            </section>

            <section className="space-y-2">
              <p className="font-medium">2.</p>
              <p>Data pribadi tersebut digunakan semata-mata untuk keperluan:</p>
              <ul className="ml-4 list-disc space-y-1 pl-2">
                <li>Pendaftaran dan pengelolaan keanggotaan ORARI;</li>
                <li>Penerbitan dan validasi Kartu Tanda Anggota (KTA);</li>
                <li>Verifikasi identitas sesuai ketentuan peraturan perundang-undangan;</li>
                <li>Administrasi organisasi serta kegiatan resmi ORARI.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <p className="font-medium">3.</p>
              <p>Saya memahami bahwa:</p>
              <ul className="ml-4 list-disc space-y-1 pl-2">
                <li>
                  Data saya akan dijaga kerahasiaannya dan dilindungi sesuai ketentuan yang berlaku,
                  termasuk mengacu pada Undang-Undang Nomor 27 Tahun 2022 tentang Perlindungan Data
                  Pribadi;
                </li>
                <li>
                  ORARI tidak akan menyebarluaskan data pribadi saya kepada pihak lain tanpa
                  persetujuan saya, kecuali diwajibkan oleh hukum atau peraturan yang berlaku,
                  kecuali dalam keadaan kahar dan/atau di luar kendali ORARI.
                </li>
              </ul>
            </section>

            <section className="space-y-2">
              <p className="font-medium">4.</p>
              <p>
                Saya bertanggung jawab atas kebenaran data yang saya berikan dan bersedia melakukan
                pembaruan apabila terdapat perubahan.
              </p>
            </section>

            <section className="space-y-2">
              <p className="font-medium">5.</p>
              <p>
                Persetujuan ini berlaku sejak saya menyatakan setuju dan tetap berlaku selama saya
                menjadi anggota ORARI atau sesuai kebutuhan administratif yang sah.
              </p>
            </section>
          </div>
        </div>

        <div className="shrink-0 space-y-4 border-t bg-background p-6">
          <div className="flex gap-3 rounded-md border bg-muted/20 p-4">
            <Checkbox
              id="personal-data-consent"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(v === true)}
              className="mt-1"
            />
            <div className="space-y-3 text-sm leading-relaxed">
              <Label htmlFor="personal-data-consent" className="cursor-pointer font-normal leading-relaxed">
                Saya menyatakan setuju memberikan data pribadi saya, termasuk KTP, kepada Organisasi
                Amatir Radio Indonesia untuk keperluan pendaftaran, verifikasi, dan pengelolaan
                keanggotaan.
              </Label>
              <p className="text-muted-foreground">
                Saya memahami bahwa data akan digunakan secara terbatas, dijaga kerahasiaannya, dan
                dilindungi sesuai Undang-Undang Nomor 27 Tahun 2022 tentang Perlindungan Data
                Pribadi.
              </p>
              <p className="font-semibold">
                Dengan ini saya menyatakan: <span className="text-foreground">SETUJU</span> (centang
                kotak di samping)
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Batal
            </Button>
            <Button type="button" disabled={!agreed} onClick={handleConfirm}>
              {confirmLabel}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
